import { useSingleLocationAlarm } from '@hakimo-ui/hakimo/data-access';
import { InvestigationViewSkeleton } from '@hakimo-ui/hakimo/feature-shared';
import { DataVersion, LocationAlarm } from '@hakimo-ui/hakimo/types';
import { NotFound, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import { PageLayout } from '@hakimo-ui/hakimo/ui-layout';
import {
  appendVersionParam,
  resolveLocationAlarmDataVersion,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LocationAlarmInvestigateView } from '../alarm-overview/LocationAlarmInvestigateView';
import { ABComparisonView } from './ab-comparison';
dayjs.extend(utc);

type Params = {
  locationAlarmId: string;
};

interface Props {
  locationAlarmId: string;
  partitionKey?: string;
  abTest?: string;
  onAlarmResolve?: () => void;
  dataVersion?: DataVersion;
}

export function LocationAlarmDetailsData(props: Props) {
  const { locationAlarmId, partitionKey, abTest, onAlarmResolve, dataVersion } =
    props;
  const navigate = useNavigate();

  const queryResult = useSingleLocationAlarm(
    locationAlarmId,
    undefined,
    partitionKey,
    dataVersion
  );

  useEffect(() => {
    if (!queryResult.data) {
      return;
    }
    if (!partitionKey) {
      const date = dayjs.utc(queryResult.data?.alarmTime);
      const calculatedPartitionKey = date.format('YYYYMM');
      const abTestParam = abTest ? `&abTest=${abTest}` : '';
      navigate(
        appendVersionParam(
          `/location-alarms/${locationAlarmId}?partitionKey=${calculatedPartitionKey}${abTestParam}`,
          dataVersion
        ),
        { replace: true }
      );
    }
  }, [
    partitionKey,
    locationAlarmId,
    navigate,
    queryResult.data,
    abTest,
    dataVersion,
  ]);

  if (queryResult.isLoading) {
    return <InvestigationViewSkeleton />;
  }

  const renderContent = (data: LocationAlarm) => {
    if (abTest === 'inference_server') {
      return <ABComparisonView key={data.id} locationAlarm={data} />;
    }
    return (
      <LocationAlarmInvestigateView
        key={data.id}
        locationAlarm={data}
        onAlarmResolve={onAlarmResolve}
        dataVersion={dataVersion}
      />
    );
  };

  return <QueryResult queryResult={queryResult}>{renderContent}</QueryResult>;
}

function LocationAlarmDetailsRoot() {
  const { locationAlarmId } = useParams<Params>();
  const [searchParams] = useSearchParams();

  const partitionKey = searchParams.get('partitionKey') ?? undefined;
  const abTest = searchParams.get('abTest') ?? undefined;
  // A copied link (see AlarmGroupHeader's onCopyLink) carries ?version=v3, so
  // it reopens against the dataset it was copied from. Never the stored
  // override: with no param the id's own shape decides, which is a property of
  // the link rather than of the viewer.
  const dataVersion = resolveLocationAlarmDataVersion(
    searchParams,
    locationAlarmId
  );

  if (locationAlarmId === undefined) {
    return <NotFound />;
  }

  return (
    <PageLayout title="Location Alarm Details" variant="single">
      <PageLayout.Content>
        <LocationAlarmDetailsData
          locationAlarmId={locationAlarmId}
          partitionKey={partitionKey}
          abTest={abTest}
          dataVersion={dataVersion}
        />
      </PageLayout.Content>
    </PageLayout>
  );
}

export default withAuthz(withErrorBoundary(LocationAlarmDetailsRoot), [
  'location_alarm/detail:view',
]);
